<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import CampaignFilters from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignFilters.vue';
import WhatsAppCsvCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCsvCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCsvCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCsvCampaignEmptyState.vue';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();

const selectedCampaign = ref(null);
const [showWhatsAppCsvCampaignDialog, toggleWhatsAppCsvCampaignDialog] =
  useToggle();
const lastRefreshTime = ref(null);

const currentPage = ref(1);
const perPage = ref(10);
const currentFilter = ref('all');
const currentSort = ref('date-desc');
const searchQuery = ref('');

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);
const isTriggering = computed(() => uiFlags.value.isTriggering);

const confirmDeleteCampaignDialogRef = ref(null);

const allWhatsAppCampaigns = computed(() =>
  getters['campaigns/getCampaigns']
    .value(CAMPAIGN_TYPES.ONE_OFF)
    .filter(campaign => campaign.inbox.channel_type === 'Channel::Whatsapp')
);

const filteredByStatus = computed(() => {
  if (currentFilter.value === 'all') {
    return allWhatsAppCampaigns.value;
  }

  return allWhatsAppCampaigns.value.filter(campaign => {
    if (currentFilter.value === 'active') {
      return campaign.campaign_status === 'active';
    }
    if (currentFilter.value === 'running') {
      return campaign.campaign_status === 'running';
    }
    if (currentFilter.value === 'completed') {
      return campaign.campaign_status === 'completed';
    }
    if (currentFilter.value === 'error') {
      return campaign.campaign_status === 'error';
    }
    return true;
  });
});

const filteredBySearch = computed(() => {
  if (!searchQuery.value) {
    return filteredByStatus.value;
  }

  const query = searchQuery.value.toLowerCase();
  return filteredByStatus.value.filter(campaign => {
    return (
      campaign.title.toLowerCase().includes(query) ||
      campaign.message.toLowerCase().includes(query) ||
      (campaign.inbox.name && campaign.inbox.name.toLowerCase().includes(query))
    );
  });
});

const sortedCampaigns = computed(() => {
  const campaigns = [...filteredBySearch.value];

  if (currentSort.value === 'date-desc') {
    return campaigns.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }
  if (currentSort.value === 'date-asc') {
    return campaigns.sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
  }
  if (currentSort.value === 'name-asc') {
    return campaigns.sort((a, b) => a.title.localeCompare(b.title));
  }
  if (currentSort.value === 'name-desc') {
    return campaigns.sort((a, b) => b.title.localeCompare(a.title));
  }

  return campaigns;
});

const paginatedCampaigns = computed(() => {
  const startIndex = (currentPage.value - 1) * perPage.value;
  const endIndex = startIndex + perPage.value;
  return sortedCampaigns.value.slice(startIndex, endIndex);
});

const totalCampaigns = computed(() => sortedCampaigns.value.length);

const hasNoWhatsAppCampaigns = computed(
  () => allWhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const refreshCampaigns = async () => {
  try {
    await store.dispatch('campaigns/get');
    lastRefreshTime.value = new Date();
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP_CSV.REFRESH.ERROR_MESSAGE'), {
      type: 'error',
    });
  }
};

const handleTrigger = async campaign => {
  try {
    await store.dispatch(
      'campaigns/trigger',
      campaign.display_id || campaign.id
    );
    await refreshCampaigns();
    useAlert(t('CAMPAIGN.WHATSAPP_CSV.TRIGGER.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error.message || t('CAMPAIGN.WHATSAPP_CSV.TRIGGER.ERROR_MESSAGE'),
      {
        type: 'error',
      }
    );
  }
};

const formattedLastRefreshTime = computed(() => {
  if (!lastRefreshTime.value) return '';

  return lastRefreshTime.value.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
});

onMounted(() => {
  refreshCampaigns();
});
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP_CSV.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP_CSV.NEW_CAMPAIGN')"
    @click="toggleWhatsAppCsvCampaignDialog()"
    @close="toggleWhatsAppCsvCampaignDialog(false)"
  >
    <template #action>
      <WhatsAppCsvCampaignDialog
        v-if="showWhatsAppCsvCampaignDialog"
        @close="toggleWhatsAppCsvCampaignDialog(false)"
      />
    </template>
    <div
      v-if="isFetchingCampaigns || isTriggering"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div v-else-if="!hasNoWhatsAppCampaigns" class="campaigns-list-container">
      <div class="flex items-center justify-between mb-3 px-1">
        <div
          v-if="lastRefreshTime"
          class="text-xs text-slate-500 flex items-center"
        >
          {{ t('CAMPAIGN.WHATSAPP_CSV.LAST_REFRESH') }}:
          {{ formattedLastRefreshTime }}
        </div>
        <button
          class="text-sm text-blue-500 hover:text-blue-700 flex items-center h-8 px-2 rounded hover:bg-blue-50"
          @click="refreshCampaigns"
        >
          <span class="flex items-center">
            <FluentIcon icon="arrow-clockwise" size="14" class="mr-1.5" />
            {{ t('CAMPAIGN.WHATSAPP_CSV.REFRESH_LIST') }}
          </span>
        </button>
      </div>

      <div class="h-px bg-n-weak dark:bg-n-weak mb-4" />

      <CampaignFilters
        v-model:current-page="currentPage"
        v-model:current-filter="currentFilter"
        v-model:current-sort="currentSort"
        v-model:search-query="searchQuery"
        :total-campaigns="totalCampaigns"
        :per-page="perPage"
      />

      <CampaignList
        :campaigns="paginatedCampaigns"
        is-whats-app-type
        @delete="handleDelete"
        @trigger="handleTrigger"
      />

      <div
        v-if="totalCampaigns === 0 && allWhatsAppCampaigns?.length > 0"
        class="text-center py-8 text-gray-500"
      >
        {{ t('CAMPAIGN.NO_RESULTS') }}
      </div>
    </div>
    <WhatsAppCsvCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP_CSV.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP_CSV.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>

<style lang="scss" scoped>
.campaigns-list-container {
  width: 100%;
}
</style>
